import { ComplementaryItem } from '@models/balance/ComplementaryItem'
import { complementaryItemSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createComplementaryItem as createComplementaryItemAPI,
  deleteComplementaryItem as deleteComplementaryItemAPI,
  fetchComplementaryItemById,
  fetchComplementaryItems,
  updateComplementaryItem as updateComplementaryItemAPI,
} from '@services/balance/ComplementaryItemService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<ComplementaryItem> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'complementaryItem',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getComplementaryItemsSuccess,
  setQuery: setComplementaryItemQuery,
  reset: resetComplementaryItemState,
  resetQueryAndIds: resetComplementaryItemQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: ComplementaryItem }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectComplementaryItems = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedComplementaryItem = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectComplementaryItemById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectComplementaryItemsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedComplementaryItem =
  (complementaryItem: ComplementaryItem | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<ComplementaryItem>(complementaryItem)
      )
    )

export const getComplementaryItems = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchComplementaryItems(params),
    [complementaryItemSchema],
    slice.actions
  )

export const createComplementaryItem = (complementaryItem: ComplementaryItem) =>
  SBAPI.CreateDispatched(
    createComplementaryItemAPI(complementaryItem),
    complementaryItemSchema,
    slice.actions
  )

export const getComplementaryItemWithId = (id: number) =>
  SBAPI.FetchDispatched<ComplementaryItem>(
    fetchComplementaryItemById(id),
    complementaryItemSchema,
    slice.actions
  )

export const updateComplementaryItem = (complementaryItem: ComplementaryItem) =>
  SBAPI.UpdateDispatched<ComplementaryItem>(
    updateComplementaryItemAPI(complementaryItem),
    complementaryItem,
    slice.actions
  )

export const deleteComplementaryItem = (complementaryItem: ComplementaryItem) =>
  SBAPI.DestroyDispatched<ComplementaryItem>(
    deleteComplementaryItemAPI(complementaryItem),
    complementaryItem,
    slice.actions
  )
